import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router";
import { Search, ChevronRight, X, Tag, Layers } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { companiesService } from "../services/companiesService";
import type { Company } from "../types";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { GlassCard } from "../components/GlassCard";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { EmptyState } from "../components/EmptyState";
import { SkeletonList } from "../components/SkeletonList";
import {
  formatBoothLabel,
  formatCompanyLocation,
  isSharedHall,
} from "../utils/locationLabel";
import {
  buildCompanyTaxonomy,
  companyInSection,
  companyMatchesTaxonomyCategory,
  formatCategoriesDisplay,
  parseCompanyTaxonomy,
} from "../utils/categoryLabel";

type NavLevel = "sections" | "categories" | "companies";
type BrowseMode = "alpha" | "sections";

export function CompaniesPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { selectedEventId, setNavbarVisible } = useAppStore();
  const [search, setSearch] = useState("");
  const [browseMode, setBrowseMode] = useState<BrowseMode>("alpha");
  const [selectedSectionKey, setSelectedSectionKey] = useState<string | null>(null);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(null);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedEventId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await companiesService.list(selectedEventId);
        if (!cancelled) setAllCompanies(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [i18n.language, selectedEventId]);

  useEffect(() => {
    setSelectedSectionKey(null);
    setSelectedCategoryKey(null);
    setSearch("");
    setBrowseMode("alpha");
  }, [selectedEventId]);

  const hideHall = useMemo(() => isSharedHall(allCompanies), [allCompanies]);

  const { sections, categoriesBySection } = useMemo(
    () => buildCompanyTaxonomy(allCompanies),
    [allCompanies],
  );

  const alphaCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...allCompanies].sort((a, b) =>
      a.name.localeCompare(b.name, i18n.language || "ru", { sensitivity: "base" }),
    );
    if (!q) return sorted;
    return sorted.filter((c) => {
      const cats = parseCompanyTaxonomy(c.category)
        .map((p) => `${p.categoryLabel} ${p.sectionTitle ?? ""}`)
        .join(" ")
        .toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        cats.includes(q) ||
        c.booth.toLowerCase().includes(q) ||
        (c.shortAbout || "").toLowerCase().includes(q)
      );
    });
  }, [allCompanies, search, i18n.language]);

  const level: NavLevel = selectedCategoryKey
    ? "companies"
    : selectedSectionKey
      ? "categories"
      : "sections";

  const inSectionsDrill = browseMode === "sections" && level !== "sections";

  const selectedSection = useMemo(
    () => sections.find((s) => s.key === selectedSectionKey) ?? null,
    [sections, selectedSectionKey],
  );

  const categoryRows = useMemo(() => {
    if (!selectedSectionKey) return [];
    return categoriesBySection.get(selectedSectionKey) ?? [];
  }, [categoriesBySection, selectedSectionKey]);

  const selectedCategory = useMemo(
    () => categoryRows.find((c) => c.key === selectedCategoryKey) ?? null,
    [categoryRows, selectedCategoryKey],
  );

  const filteredSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || level !== "sections") return sections;
    return sections.filter((s) => {
      if (s.title.toLowerCase().includes(q)) return true;
      const cats = categoriesBySection.get(s.key) ?? [];
      return cats.some((c) => c.label.toLowerCase().includes(q));
    });
  }, [sections, search, level, categoriesBySection]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || level !== "categories") return categoryRows;
    return categoryRows.filter((c) => c.label.toLowerCase().includes(q));
  }, [categoryRows, search, level]);

  /** Section has no leaf categories — list companies tagged with the section itself. */
  const sectionOnlyCompanies = useMemo(() => {
    if (level !== "categories" || categoryRows.length > 0 || !selectedSectionKey) {
      return [];
    }
    const q = search.trim().toLowerCase();
    return allCompanies.filter((c) => {
      if (!companyInSection(c.category, selectedSectionKey)) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.booth.toLowerCase().includes(q) ||
        (c.shortAbout || "").toLowerCase().includes(q)
      );
    });
  }, [level, categoryRows.length, selectedSectionKey, allCompanies, search]);

  const companies = useMemo(() => {
    if (!selectedCategoryKey) return [];
    const q = search.trim().toLowerCase();
    return allCompanies.filter((c) => {
      if (!companyMatchesTaxonomyCategory(c.category, selectedCategoryKey)) return false;
      if (!q) return true;
      const cats = parseCompanyTaxonomy(c.category)
        .map((p) => p.categoryLabel)
        .join(" ")
        .toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        cats.includes(q) ||
        c.booth.toLowerCase().includes(q) ||
        (c.shortAbout || "").toLowerCase().includes(q)
      );
    });
  }, [allCompanies, search, selectedCategoryKey]);

  /** Global search shortcut: from sections level, also list matching companies. */
  const searchCompanies = useMemo(() => {
    if (level !== "sections") return [];
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return allCompanies
      .filter((c) => {
        const cats = parseCompanyTaxonomy(c.category)
          .map((p) => `${p.categoryLabel} ${p.sectionTitle ?? ""}`)
          .join(" ")
          .toLowerCase();
        return (
          c.name.toLowerCase().includes(q) ||
          cats.includes(q) ||
          c.booth.toLowerCase().includes(q)
        );
      })
      .slice(0, 30);
  }, [allCompanies, search, level]);

  const headerTitle =
    browseMode === "alpha"
      ? t("companies.title")
      : level === "companies"
        ? selectedCategory?.label ?? t("companies.title")
        : level === "categories"
          ? selectedSection?.title ?? t("companies.title")
          : t("companies.title");

  const headerSubtitle = loading
    ? t("companies.subtitle_loading")
    : browseMode === "alpha"
      ? t("companies.alpha_subtitle", { n: alphaCompanies.length })
      : level === "companies"
        ? t("companies.subtitle", { n: companies.length })
        : level === "categories"
          ? categoryRows.length === 0
            ? t("companies.subtitle", { n: sectionOnlyCompanies.length })
            : t("companies.categories_subtitle", {
                defaultValue: "{{n}} категорий",
                n: filteredCategories.length,
              })
          : t("companies.sections_subtitle", {
              n: filteredSections.length,
            });

  const goBack = () => {
    setSearch("");
    if (level === "companies") {
      setSelectedCategoryKey(null);
      return;
    }
    if (level === "categories") {
      setSelectedSectionKey(null);
      return;
    }
  };

  const switchBrowseMode = (mode: BrowseMode) => {
    setBrowseMode(mode);
    setSearch("");
    setSelectedSectionKey(null);
    setSelectedCategoryKey(null);
  };

  const searchPlaceholder =
    browseMode === "alpha"
      ? t("companies.placeholder")
      : level === "companies"
        ? t("companies.placeholder")
        : level === "categories"
          ? categoryRows.length === 0
            ? t("companies.placeholder")
            : t("companies.categories_placeholder", {
                defaultValue: "Поиск категории…",
              })
          : t("companies.sections_placeholder", {
              defaultValue: "Поиск раздела или компании…",
            });

  return (
    <>
      <PageHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        back
        backTo={inSectionsDrill ? undefined : "/home"}
        onBack={inSectionsDrill ? goBack : undefined}
      />
      <PageShell>
        {!inSectionsDrill ? (
          <div className="mt-3 mb-2 flex bg-gray-100 rounded-xl p-0.5">
            {(
              [
                { id: "alpha" as const, label: t("companies.tab_alpha") },
                { id: "sections" as const, label: t("companies.tab_sections") },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => switchBrowseMode(tab.id)}
                className={`flex-1 py-2 rounded-[10px] text-[13px] transition-colors ${
                  browseMode === tab.id
                    ? "bg-white shadow-sm text-indigo-600"
                    : "text-gray-500"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className={`${inSectionsDrill ? "mt-3" : ""} mb-3`}>
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setNavbarVisible(false)}
              onBlur={() => setNavbarVisible(true)}
              placeholder={searchPlaceholder}
              className="w-full h-10 pl-10 pr-10 rounded-xl bg-gray-100/80 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-200 transition-shadow"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X size={16} className="text-gray-400" />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <SkeletonList count={8} variant="row" className="mt-1" />
        ) : browseMode === "alpha" ? (
          alphaCompanies.length === 0 ? (
            <EmptyState
              icon={<Search size={28} />}
              title={t("companies.empty_title")}
              description={t("companies.empty_desc")}
            />
          ) : (
            <div className="space-y-2">
              {alphaCompanies.map((company, i) => (
                <CompanyRow
                  key={company.id}
                  company={company}
                  hideHall={hideHall}
                  index={i}
                  onOpen={() => navigate(`/companies/${company.id}`)}
                />
              ))}
            </div>
          )
        ) : level === "sections" ? (
          filteredSections.length === 0 && searchCompanies.length === 0 ? (
            <EmptyState
              icon={<Layers size={28} />}
              title={t("companies.empty_title")}
              description={t("companies.empty_desc")}
            />
          ) : (
            <div className="space-y-2">
              {filteredSections.map((row, i) => (
                <motion.div
                  key={row.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i, 12) * 0.03, duration: 0.25 }}
                >
                  <GlassCard
                    variant="default"
                    padding="sm"
                    className="cursor-pointer active:scale-[0.98] transition-transform"
                    onClick={() => {
                      setSelectedSectionKey(row.key);
                      setSelectedCategoryKey(null);
                      setSearch("");
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                        <Layers size={18} className="text-violet-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] text-gray-900 leading-snug">{row.title}</p>
                        <p className="text-[12px] text-gray-400 mt-0.5">
                          {row.categoryCount > 0
                            ? t("companies.section_meta", {
                                defaultValue: "{{cats}} кат. · {{n}} компаний",
                                cats: row.categoryCount,
                                n: row.companyCount,
                              })
                            : t("companies.subtitle", { n: row.companyCount })}
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                    </div>
                  </GlassCard>
                </motion.div>
              ))}

              {searchCompanies.length > 0 ? (
                <div className="pt-3">
                  <p className="text-[12px] text-gray-500 px-1 mb-2">
                    {t("companies.search_companies", {
                      defaultValue: "Компании",
                    })}
                  </p>
                  {searchCompanies.map((company, i) => {
                    const location = formatCompanyLocation({
                      booth: company.booth,
                      hall: company.hall,
                      hideHall,
                    });
                    return (
                      <motion.div
                        key={company.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(i, 8) * 0.03, duration: 0.25 }}
                        className="mb-2"
                      >
                        <GlassCard
                          variant="default"
                          padding="sm"
                          className="cursor-pointer active:scale-[0.98] transition-transform"
                          onClick={() => navigate(`/companies/${company.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <CompanyAvatar
                              name={company.name}
                              logoUrl={company.logoUrl}
                              size="lg"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] text-gray-900 truncate">{company.name}</p>
                              {location ? (
                                <p className="text-[12px] text-gray-500 mt-0.5 truncate">
                                  {location}
                                </p>
                              ) : null}
                            </div>
                            <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                          </div>
                        </GlassCard>
                      </motion.div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          )
        ) : level === "categories" ? (
          categoryRows.length === 0 ? (
            // Section with only section-level tags and no leaf categories — show companies in section
            sectionOnlyCompanies.length === 0 ? (
              <EmptyState
                icon={<Tag size={28} />}
                title={t("companies.empty_title")}
                description={t("companies.empty_desc")}
              />
            ) : (
              <div className="space-y-2">
                <p className="text-[12px] text-gray-500 px-1 mb-1">
                  {t("companies.section_companies_hint", {
                    section: selectedSection?.title ?? headerTitle,
                    defaultValue: "Компании раздела «{{section}}»",
                  })}
                </p>
                {sectionOnlyCompanies.map((company, i) => (
                  <CompanyRow
                    key={company.id}
                    company={company}
                    hideHall={hideHall}
                    index={i}
                    onOpen={() => navigate(`/companies/${company.id}`)}
                  />
                ))}
              </div>
            )
          ) : filteredCategories.length === 0 ? (
            <EmptyState
              icon={<Tag size={28} />}
              title={t("companies.empty_title")}
              description={t("companies.empty_desc")}
            />
          ) : (
            <div className="space-y-2">
              {filteredCategories.map((row, i) => (
                <motion.div
                  key={row.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i, 12) * 0.03, duration: 0.25 }}
                >
                  <GlassCard
                    variant="default"
                    padding="sm"
                    className="cursor-pointer active:scale-[0.98] transition-transform"
                    onClick={() => {
                      setSelectedCategoryKey(row.key);
                      setSearch("");
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <Tag size={18} className="text-indigo-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] text-gray-900 leading-snug">{row.label}</p>
                        <p className="text-[12px] text-gray-400 mt-0.5">
                          {row.codes.length > 0
                            ? `${row.codes.join(" · ")} · ${t("companies.subtitle", { n: row.companyCount })}`
                            : t("companies.subtitle", { n: row.companyCount })}
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                    </div>
                  </GlassCard>
                </motion.div>
              ))}
            </div>
          )
        ) : companies.length === 0 ? (
          <EmptyState
            icon={<Search size={28} />}
            title={t("companies.empty_title")}
            description={t("companies.empty_desc")}
          />
        ) : (
          <div className="space-y-2">
            {companies.map((company, i) => (
              <CompanyRow
                key={company.id}
                company={company}
                hideHall={hideHall}
                index={i}
                onOpen={() => navigate(`/companies/${company.id}`)}
              />
            ))}
          </div>
        )}
      </PageShell>
    </>
  );
}

function CompanyRow({
  company,
  hideHall,
  index,
  onOpen,
}: {
  company: Company;
  hideHall: boolean;
  index: number;
  onOpen: () => void;
}) {
  const location = formatCompanyLocation({
    booth: company.booth,
    hall: company.hall,
    hideHall,
  });
  const categoryLine = formatCategoriesDisplay(company.category);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 12) * 0.03, duration: 0.25 }}
    >
      <GlassCard
        variant="default"
        padding="sm"
        className="cursor-pointer active:scale-[0.98] transition-transform"
        onClick={onOpen}
      >
        <div className="flex items-center gap-3">
          <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="lg" />
          <div className="flex-1 min-w-0">
            <p className="text-[15px] text-gray-900 truncate">{company.name}</p>
            {location ? (
              <p className="text-[12px] text-gray-500 mt-0.5 truncate">{location}</p>
            ) : formatBoothLabel(company.booth) ? (
              <p className="text-[12px] text-gray-500 mt-0.5">
                {formatBoothLabel(company.booth)}
              </p>
            ) : null}
            {categoryLine ? (
              <p className="text-[12px] text-gray-400 truncate mt-0.5">{categoryLine}</p>
            ) : null}
          </div>
          <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
        </div>
      </GlassCard>
    </motion.div>
  );
}
